/*
 * Cart drawer upsell (markup: snippets/cart-upsell.liquid).
 *
 * The list is fetched ONCE per browsing session and then never rebuilt:
 * with items in the cart it comes from the recommendations endpoint seeded
 * with the OLDEST non-gift line (recommendations never include their own
 * seed, so seeding with the newest line would drop the just-added card);
 * with an empty cart it comes from the fallback section. Cart changes only
 * toggle the per-card in-cart state (check on the add button, controls
 * disabled) - cards are never recreated, nothing jumps under the pointer,
 * and a product removed from the cart is addable again immediately. The
 * list persists in sessionStorage, so a reload restores the exact same
 * cards; after Dawn replaces the drawer markup the fresh elements are
 * refilled synchronously from the cache. The auto-added free gift line
 * does not count as "in cart": the same product stays addable as a
 * regular paid item.
 */
class CartUpsell extends HTMLElement {
  static booted = false;
  static cartPromise = null;
  static cartAt = 0;
  static cache = { html: null };
  static loading = null;
  static STORE_KEY = 'wf-cart-upsell-list-v7';

  static restoreCache() {
    if (CartUpsell.cache.html) return;
    try {
      const stored = JSON.parse(sessionStorage.getItem(CartUpsell.STORE_KEY));
      if (stored && typeof stored.html === 'string' && stored.html && Date.now() - (stored.at || 0) < 600000) {
        CartUpsell.cache = { html: stored.html };
      }
    } catch {
      /* no stored list */
    }
  }

  static persistCache() {
    try {
      sessionStorage.setItem(
        CartUpsell.STORE_KEY,
        JSON.stringify({
          html: CartUpsell.cache.html,
          at: Date.now(),
        })
      );
    } catch {
      /* storage unavailable: the list refetches on reload */
    }
  }

  connectedCallback() {
    CartUpsell.boot();
    CartUpsell.restoreCache();
    // Fresh element after a drawer re-render: restore the cached list in the
    // same frame so the cards do not visibly rebuild, and re-apply the last
    // known in-cart states right away - waiting for the cart fetch made
    // every check flash back to a plus for a moment.
    if (CartUpsell.cache.html) {
      this.inject(CartUpsell.cache.html);
      this.updateStates();
    }
    CartUpsell.sync();
  }

  static boot() {
    if (CartUpsell.booted) return;
    CartUpsell.booted = true;

    // Load when the drawer opens (the drawer element survives re-renders,
    // only its inner markup is replaced).
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(() => {
        if (drawer.classList.contains('active')) CartUpsell.sync();
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    // PUB_SUB_EVENTS / subscribe are top-level declarations in Dawn, not
    // window properties, hence the typeof guards.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        // Bust the short-lived cart cache: a promise fetched just before the
        // add would report the pre-add cart and roll the checks back.
        CartUpsell.cartAt = 0;
        CartUpsell.sync();
      });
    }

    // Cart button + variant chooser. Everything runs in the CAPTURE phase
    // and is stopped there, so Dawn's drawer handlers (overlay close,
    // cart-items change/keyup listeners) never see these events.
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!target || !target.closest) return;

        // Several in-stock variants: the cart button only opens the chooser
        // (preventDefault keeps the submit button from adding).
        const toggle = target.closest('.wf-upsell__add[data-chooser]');
        if (toggle) {
          event.preventDefault();
          event.stopPropagation();
          if (toggle.getAttribute('aria-disabled') === 'true') return;
          const open = toggle.getAttribute('aria-expanded') === 'true';
          CartUpsell.closeChoosers();
          if (!open) CartUpsell.openChooser(toggle);
          return;
        }

        // Direct add button: check live stock before the add request.
        const direct = target.closest('.wf-upsell__add:not([data-chooser])');
        if (direct && direct.dataset.checked !== 'true') {
          event.preventDefault();
          event.stopPropagation();
          if (direct.getAttribute('aria-disabled') !== 'true') CartUpsell.checkDirectAdd(direct);
          return;
        }

        // A variant picked in the chooser: select it and add it.
        const option = target.closest('.wf-upsell__opt');
        if (option) {
          event.preventDefault();
          event.stopPropagation();
          if (option.disabled) return;
          CartUpsell.chooseVariant(option);
          return;
        }

        // Click anywhere else closes an open chooser (outside its card).
        const openChooser = document.querySelector('.wf-upsell__chooser:not([hidden])');
        if (openChooser && !openChooser.contains(target)) CartUpsell.closeChoosers();
      },
      true
    );

    // Esc closes the chooser and must not also close the whole drawer
    // (Dawn's cart-drawer closes on keyup Escape).
    const onEscape = (event) => {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;
      const openChooser = document.querySelector('.wf-upsell__chooser:not([hidden])');
      if (!openChooser) return;
      event.stopPropagation();
      if (event.type === 'keydown') CartUpsell.closeChoosers(true);
    };
    document.addEventListener('keydown', onEscape, true);
    document.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;
      if (CartUpsell.escapeHandledAt && Date.now() - CartUpsell.escapeHandledAt < 500) event.stopPropagation();
    }, true);
  }

  static openChooser(toggle) {
    const form = toggle.closest('form');
    const chooser = form && form.querySelector('.wf-upsell__chooser');
    if (!chooser) return;
    const card = toggle.closest('.wf-upsell__card');
    chooser.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    if (card) card.classList.add('is-choosing');

    // Open upwards when there is no room below inside the scrolling list
    // (the last cards of the desktop panel, the bottom of the mobile drawer).
    chooser.classList.remove('is-up');
    const scroller = toggle.closest('.wf-upsell__list, .drawer__contents, .drawer__inner') || document.documentElement;
    const limit = Math.min(scroller.getBoundingClientRect().bottom, window.innerHeight);
    if (chooser.getBoundingClientRect().bottom > limit - 8) chooser.classList.add('is-up');

    const first = chooser.querySelector('.wf-upsell__opt:not([disabled])');
    if (first) first.focus({ preventScroll: true });

    // The card markup is cached for the whole session, so stock may have
    // changed since it was rendered: re-check the chips against live data.
    CartUpsell.refreshChips(chooser, card);
  }

  static liveVariants(handle) {
    CartUpsell.stock = CartUpsell.stock || {};
    const known = CartUpsell.stock[handle];
    if (known && Date.now() - known.at < 15000) return known.promise;
    // Section render, not /products/<handle>.js: `available` there is always
    // true for continue-selling variants, so it cannot show a size at 0 stock.
    const request = fetch(`/products/${encodeURIComponent(handle)}?section_id=cart-upsell-stock`)
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        const match = html && html.match(/<script[^>]*data-upsell-stock[^>]*>([\s\S]*?)<\/script>/);
        const data = match ? JSON.parse(match[1]) : null;
        return data && Array.isArray(data.variants)
          ? new Map(data.variants.map((variant) => [String(variant.id), variant.in_stock]))
          : null;
      })
      .catch(() => null);
    CartUpsell.stock[handle] = { at: Date.now(), promise: request };
    return request;
  }

  static cardHandle(card) {
    const link = card && card.querySelector('a.wf-upsell__media, a.wf-upsell__name');
    return link ? (link.getAttribute('href') || '').split('?')[0].split('/products/')[1] : '';
  }

  static refreshChips(chooser, card) {
    const handle = CartUpsell.cardHandle(card);
    if (!handle) return;
    CartUpsell.liveVariants(handle).then((live) => {
      if (!live) return;
      chooser.querySelectorAll('.wf-upsell__opt').forEach((option) => {
        if (live.get(option.dataset.variantId) === false) option.disabled = true;
      });
    });
  }

  // Direct add (single in-stock variant): re-check stock first, the cached
  // card may be stale. If still available, the submit is replayed.
  static checkDirectAdd(button) {
    const form = button.closest('form');
    const handle = CartUpsell.cardHandle(button.closest('.wf-upsell__card'));
    const idInput = form && form.querySelector('[name="id"]');
    if (!handle || !idInput) return;
    CartUpsell.liveVariants(handle).then((live) => {
      if (live && live.get(String(idInput.value)) === false) {
        button.setAttribute('aria-disabled', 'true');
        const wrapper = form.querySelector('.wf-upsell__error');
        const text = wrapper && wrapper.querySelector('.product-form__error-message');
        if (text) text.textContent = 'Sold out';
        if (wrapper) wrapper.hidden = false;
        return;
      }
      button.dataset.checked = 'true';
      if (typeof form.requestSubmit === 'function') form.requestSubmit(button);
      else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      delete button.dataset.checked;
    });
  }

  static closeChoosers(restoreFocus = false) {
    document.querySelectorAll('.wf-upsell__chooser:not([hidden])').forEach((chooser) => {
      chooser.hidden = true;
      const form = chooser.closest('form');
      const toggle = form && form.querySelector('.wf-upsell__add[data-chooser]');
      const card = chooser.closest('.wf-upsell__card');
      if (card) card.classList.remove('is-choosing');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        if (restoreFocus) toggle.focus({ preventScroll: true });
      }
    });
    if (restoreFocus) CartUpsell.escapeHandledAt = Date.now();
  }

  static chooseVariant(option) {
    const form = option.closest('form');
    if (!form) return;
    const idInput = form.querySelector('[name="id"]');
    if (idInput) idInput.value = option.dataset.variantId;
    CartUpsell.showPrice(form, option.dataset.price, option.dataset.compare);
    CartUpsell.closeChoosers(true);
    const submit = form.querySelector('.wf-upsell__add');
    // Same path as a direct add: Dawn's <product-form> handles the submit.
    if (submit && typeof form.requestSubmit === 'function') form.requestSubmit(submit);
    else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  }

  static showPrice(form, priceText, compareText) {
    const price = form.querySelector('[data-upsell-price]');
    if (!price || !priceText) return;
    price.textContent = '';
    const now = document.createElement('span');
    now.className = 'wf-upsell__now';
    now.textContent = priceText;
    price.append(now);
    if (compareText) {
      const old = document.createElement('s');
      old.className = 'wf-upsell__old';
      old.textContent = compareText;
      price.append(old);
    }
  }

  // One cart fetch shared by all callers within a short window.
  static fetchCart() {
    const now = Date.now();
    if (!CartUpsell.cartPromise || now - CartUpsell.cartAt > 300) {
      CartUpsell.cartAt = now;
      CartUpsell.cartPromise = fetch(`${window.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
    }
    return CartUpsell.cartPromise;
  }

  static async sync() {
    const cart = await CartUpsell.fetchCart();
    const items = (cart && cart.items) || [];

    if (!CartUpsell.cache.html) await CartUpsell.load(items);

    // No "added" state: every card stays addable, adding again raises the
    // line quantity. Only the empty state is kept in sync.
    CartUpsell.persistCache();
    document.querySelectorAll('cart-upsell').forEach((el) => el.updateStates());

    // A drawer re-render can leave focus on a control inside the panel;
    // blur it so mobile browsers do not pop a select open.
    const active = document.activeElement;
    if (active && active.closest && active.closest('cart-upsell')) active.blur();
  }

  static load(items) {
    if (CartUpsell.loading) return CartUpsell.loading;
    const any = document.querySelector('cart-upsell');
    if (!any) return Promise.resolve();
    const nonGift = items.filter((item) => !(item.properties && item.properties._free_gift));
    const seedItem = nonGift[nonGift.length - 1] || items[items.length - 1];
    const url = seedItem
      ? `${any.dataset.recommendationsUrl}&product_id=${seedItem.product_id}`
      : any.dataset.sectionUrl;
    document.querySelectorAll('cart-upsell').forEach((el) => el.classList.add('is-loading'));
    CartUpsell.loading = fetch(url)
      .then(async (response) => {
        if (!response.ok) throw new Error(`cart-upsell: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('.wf-upsell__list');
        if (!fresh) return;
        CartUpsell.cache = { html: fresh.innerHTML };
        CartUpsell.persistCache();
        document.querySelectorAll('cart-upsell').forEach((el) => el.inject(fresh.innerHTML));
      })
      .catch(() => {})
      .finally(() => {
        CartUpsell.loading = null;
        document.querySelectorAll('cart-upsell').forEach((el) => el.classList.remove('is-loading'));
      });
    return CartUpsell.loading;
  }

  inject(html) {
    const list = this.querySelector('.wf-upsell__list');
    if (list) list.innerHTML = html;
  }

  updateStates() {
    const list = this.querySelector('.wf-upsell__list');
    if (!list) return;
    // Dawn's product-form marks every submit button aria-haspopup="dialog"
    // (it opens the drawer); a chooser button opens a list instead.
    list.querySelectorAll('.wf-upsell__add[data-chooser]').forEach((button) => {
      button.setAttribute('aria-haspopup', 'true');
    });
    this.classList.toggle('is-empty', list.querySelectorAll('.wf-upsell__item').length === 0);
  }
}

customElements.define('cart-upsell', CartUpsell);
